/**
 * Global Cmd+K (Ctrl+K) command palette for Relay.
 *
 * Opens from anywhere via the keyboard shortcut or the `relay:open-search`
 * window event (dispatched by the sidebar trigger). Under two characters it
 * lists recently opened records; otherwise it searches every workspace object
 * type (ADR-001 D6), grouped in registry order. The arrow keys move across
 * whichever list is showing, and Enter opens the active row.
 *
 * Mounted once in AppShell so it's available on every authenticated page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckSquare,
  CircleDollarSign,
  FileText,
  FolderKanban,
  Search,
  StickyNote,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganization } from "@/contexts/OrganizationContext";
import { WORKSPACE_OBJECTS, WORKSPACE_OBJECT_TYPES } from "@/lib/workspace-objects";
import { cn } from "@/lib/utils";
import { useRecents } from "./recents";
import {
  ENTITY_LABELS,
  isSearchable,
  searchTerm,
  useGlobalSearch,
  type SearchEntityType,
  type SearchResult,
} from "./useGlobalSearch";

/** Window event other components can dispatch to open the palette. */
export const OPEN_SEARCH_EVENT = "relay:open-search";

const ICONS: Record<SearchEntityType, typeof User> = {
  contact: User,
  company: Building2,
  deal: CircleDollarSign,
  project: FolderKanban,
  document: FileText,
  note: StickyNote,
  task: CheckSquare,
};

type Group = { key: string; heading: string; items: SearchResult[] };

const listFormat = new Intl.ListFormat("en", { type: "conjunction" });
const PLURAL_LABELS = new Map<string, string>(Object.entries(ENTITY_LABELS));

export function GlobalSearchPalette({
  confirmNavigate = () => true,
}: {
  /**
   * Gate the palette's navigate-to-result action behind the app-wide
   * unsaved-changes guard. Defaults to a no-op that always allows
   * navigating, so this component works standalone outside AppShell.
   */
  confirmNavigate?: () => boolean;
} = {}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const { recents, push: remember, refresh: refreshRecents } = useRecents();
  const listRef = useRef<HTMLDivElement>(null);

  // Global open shortcut (Cmd/Ctrl+K) + programmatic open event.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener(OPEN_SEARCH_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(OPEN_SEARCH_EVENT, onOpen);
    };
  }, []);

  // Reset transient state when closing; re-read recents when opening.
  useEffect(() => {
    if (open) {
      refreshRecents();
    } else {
      setTerm("");
      setDebounced("");
      setActiveIndex(0);
    }
  }, [open, refreshRecents]);

  // Debounce the query that hits the network.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term), 180);
    return () => clearTimeout(id);
  }, [term]);

  const trimmed = searchTerm(debounced);
  // Follows what's typed, not the debounced search: once a term is searchable
  // the recents are gone, and Enter waits for that term's results.
  const showingRecents = !isSearchable(term);
  const pending = !showingRecents && searchTerm(term) !== trimmed;
  const { data, isFetching } = useGlobalSearch(debounced, organizationId, open);
  const failed = showingRecents ? [] : data?.failed ?? [];
  const failedLabels = failed.map((type) => PLURAL_LABELS.get(type) ?? type);

  // One flat list for keyboard navigation, grouped for render: recents, or
  // results in registry order.
  const { flat, groups } = useMemo(() => {
    if (showingRecents) {
      const items = recents.map((r): SearchResult => ({
        type: r.type,
        id: r.id,
        label: r.label,
        to: r.to ?? WORKSPACE_OBJECTS[r.type].routeOf({ id: r.id }),
      }));
      return { flat: items, groups: items.length ? [{ key: "recent", heading: "Recent", items }] : [] };
    }
    const results = data?.results ?? [];
    const grouped: Group[] = WORKSPACE_OBJECT_TYPES.map((type) => ({
      key: type,
      heading: ENTITY_LABELS[type],
      items: results.filter((r) => r.type === type),
    })).filter((g) => g.items.length > 0);
    return { flat: grouped.flatMap((g) => g.items), groups: grouped };
  }, [showingRecents, recents, data]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced, data, showingRecents]);

  const go = useCallback(
    (item: SearchResult | undefined) => {
      if (!item) return;
      if (!confirmNavigate()) return;
      remember({ type: item.type, id: item.id, label: item.label, to: item.to });
      setOpen(false);
      navigate(item.to);
    },
    [navigate, confirmNavigate, remember],
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && pending) {
      e.preventDefault();
      return;
    }
    if (flat.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat.at(activeIndex));
    }
  };

  // Keep the active row in view as it changes.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="top-[12%] max-w-xl translate-y-0 gap-0 overflow-hidden p-0"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Search Relay</DialogTitle>

        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search contacts, deals, projects, documents, notes…"
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            aria-label="Search"
          />
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-2">
          {failedLabels.length > 0 && (
            <p role="status" className="px-4 pb-2 text-xs text-destructive">
              Couldn&apos;t search {listFormat.format(failedLabels)}. Results may be incomplete.
            </p>
          )}
          {flat.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              {showingRecents
                ? "Type at least 2 characters to search."
                : pending || isFetching
                  ? "Searching…"
                  : `No results for “${trimmed}”.`}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.key} className="mb-1">
                <p className="px-4 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {group.heading}
                </p>
                {group.items.map((item) => {
                  const idx = flat.indexOf(item);
                  const Icon = ICONS[item.type];
                  return (
                    <button
                      key={`${item.type}:${item.id}`}
                      type="button"
                      data-index={idx}
                      onClick={() => go(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={cn(
                        "flex w-full items-center gap-3 px-4 py-2 text-left text-sm",
                        idx === activeIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.sublabel && (
                        <span className="truncate text-xs text-muted-foreground">{item.sublabel}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
